import React, { useState } from 'react';
import { Card, Button, Table, Badge, Alert, Modal, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useCourses } from '@hooks';
import { CourseEditorModal } from '@components/admin/CourseEditorModal';
import { useAuth } from '@context';
import { getManagedCourses, isAdmin, isCourseManager, hasCapability } from '@utils/permissions';

export function CoursesPage() {
  const { courses, loading, error, createCourse, updateCourse, deleteCourse } = useCourses();
  const { user, updateUser: updateAuthUser } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const scopedCourses = getManagedCourses(user, courses);
  const userIsAdmin = isAdmin(user);
  const userIsCourseManager = isCourseManager(user);
  const canSuspend = hasCapability(user, 'suspend_courses');
  const managerManagedCourseCount = Array.isArray(user?.managedCourses) ? user.managedCourses.length : 0;
  const managerCourseLicenseLimit = Number(user?.courseLicenseLimit ?? 0);
  const managerCanCreateCourse = userIsCourseManager
    && (managerCourseLicenseLimit <= 0 || managerManagedCourseCount < managerCourseLicenseLimit);
  const canCreateCourse = userIsAdmin || managerCanCreateCourse;
  const courseCreationLimitReached = userIsCourseManager && !managerCanCreateCourse;

  const [editingCourse, setEditingCourse] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Suspend modal state
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendSaving, setSuspendSaving] = useState(false);

  /**
   * Handle creating a new course
   */
  const handleCreateClick = () => {
    if (!canCreateCourse) {
      setActionError('Course license limit reached. You cannot create additional courses.');
      return;
    }

    setEditingCourse(null);
    setShowModal(true);
    setActionError(null);
  };

  /**
   * Handle editing an existing course
   */
  const handleEditClick = (course) => {
    setEditingCourse(course);
    setShowModal(true);
    setActionError(null);
  };

  /**
   * Handle saving course (create or update)
   */
  const handleSave = async (formData) => {
    try {
      setActionError(null);
      if (editingCourse) {
        await updateCourse(editingCourse.id, formData);
        setSuccessMessage('Course updated successfully');
      } else {
        const createdCourse = await createCourse(formData);
        if (userIsCourseManager && createdCourse?.id) {
          const currentManagedCourses = Array.isArray(user?.managedCourses) ? user.managedCourses : [];
          if (!currentManagedCourses.includes(createdCourse.id)) {
            updateAuthUser({
              managedCourses: [...currentManagedCourses, createdCourse.id],
            });
          }
        }
        setSuccessMessage('Course created successfully');
      }
      setShowModal(false);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setActionError(err.message || 'Failed to save course');
      throw err; // Re-throw to let modal handle it
    }
  };

  /**
   * Handle deleting a course
   */
  const handleDelete = async (course) => {
    if (window.confirm(`Are you sure you want to delete "${course.title}"? This action cannot be undone.`)) {
      try {
        setActionError(null);
        await deleteCourse(course.id);
        setSuccessMessage('Course deleted successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err) {
        setActionError(err.message || 'Failed to delete course');
      }
    }
  };

  /**
   * Open suspend modal
   */
  const handleSuspendClick = (course) => {
    setSuspendTarget(course);
    setSuspendReason(course.suspensionReason || '');
    setShowSuspendModal(true);
  };

  /**
   * Confirm suspend course
   */
  const handleSuspendConfirm = async () => {
    if (!suspendTarget) return;
    try {
      setSuspendSaving(true);
      setActionError(null);
      await updateCourse(suspendTarget.id, {
        isSuspended: true,
        suspensionReason: suspendReason.trim() || null,
      });
      setSuccessMessage(`Course "${suspendTarget.title}" has been suspended.`);
      setTimeout(() => setSuccessMessage(null), 4000);
      setShowSuspendModal(false);
      setSuspendTarget(null);
      setSuspendReason('');
    } catch (err) {
      setActionError(err.message || 'Failed to suspend course');
    } finally {
      setSuspendSaving(false);
    }
  };

  /**
   * Unsuspend a course
   */
  const handleUnsuspend = async (course) => {
    try {
      setActionError(null);
      await updateCourse(course.id, {
        isSuspended: false,
        suspensionReason: null,
      });
      setSuccessMessage(`Course "${course.title}" has been reactivated.`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setActionError(err.message || 'Failed to reactivate course');
    }
  };

  /**
   * Calculate course statistics
   */
  const getCourseStats = (course) => {
    const sections = course.sections || [];
    const sectionCount = sections.length;
    const questionCount = sections.reduce((sum, s) => sum + ((s.questions || []).length), 0);
    const contentCount = sections.reduce((sum, s) => sum + ((s.examples || []).length), 0);

    return { sectionCount, questionCount, contentCount };
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">📚 Course Management</h4>
          <small className="text-muted">
            Create and manage courses, modules, content, and questions.
            {userIsCourseManager && managerCourseLicenseLimit > 0 && (
              <span className={`ms-2 ${courseCreationLimitReached ? 'text-warning' : ''}`}>
                Course licenses used: {managerManagedCourseCount}/{managerCourseLicenseLimit}
              </span>
            )}
          </small>
        </div>
        {canCreateCourse && (
          <Button variant="primary" onClick={handleCreateClick}>
            + New Course
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setActionError(null)}>
          {error}
        </Alert>
      )}

      {actionError && (
        <Alert variant="danger" dismissible onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          {scopedCourses.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <div style={{ fontSize: '3rem' }} className="mb-3">📚</div>
              <h5>No courses yet</h5>
              <p className="mb-3">
                {userIsAdmin
                  ? 'Get started by creating your first course.'
                  : userIsCourseManager
                    ? 'No assigned courses are available yet.'
                    : 'No assigned courses are available yet.'}
              </p>
              {canCreateCourse && (
                <Button variant="primary" onClick={handleCreateClick}>
                  + Create Your First Course
                </Button>
              )}
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Title</th>
                  <th>Description</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Max Students</th>
                  <th className="text-center">Modules</th>
                  <th className="text-center">Content</th>
                  <th className="text-center">Questions</th>
                  <th style={{ width: 260 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {scopedCourses.map(course => {
                  const stats = getCourseStats(course);
                  return (
                    <tr key={course.id} className={course.isSuspended ? 'table-warning' : ''}>
                      <td>
                        <strong>{course.title}</strong>
                      </td>
                      <td>
                        <div className="text-muted small" style={{ maxWidth: 300 }}>
                          {course.description 
                            ? (course.description.length > 100 
                                ? `${course.description.substring(0, 100)}...` 
                                : course.description)
                            : <span className="text-muted fst-italic">No description</span>
                          }
                        </div>
                      </td>
                      <td className="text-center">
                        {course.isSuspended
                          ? <Badge bg="warning" text="dark">Suspended</Badge>
                          : <Badge bg="success">Active</Badge>
                        }
                      </td>
                      <td className="text-center">
                        <span className="text-muted small">
                          {course.maxStudents != null ? course.maxStudents : '∞'}
                        </span>
                      </td>
                      <td className="text-center">
                        <Badge bg="info">{stats.sectionCount}</Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="secondary">{stats.contentCount}</Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="primary">{stats.questionCount}</Badge>
                      </td>
                      <td>
                        <Button 
                          size="sm" 
                          variant="outline-primary" 
                          as={Link}
                          to={`/admin/courses/${course.id}`}
                          className="me-1"
                        >
                          Manage
                        </Button>
                        {userIsAdmin && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline-secondary"
                              onClick={() => handleEditClick(course)}
                              className="me-1"
                            >
                              Edit
                            </Button>
                            {canSuspend && (
                              course.isSuspended ? (
                                <Button
                                  size="sm"
                                  variant="outline-success"
                                  onClick={() => handleUnsuspend(course)}
                                  className="me-1"
                                  title="Reactivate course"
                                >
                                  Activate
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline-warning"
                                  onClick={() => handleSuspendClick(course)}
                                  className="me-1"
                                  title="Suspend course"
                                >
                                  Suspend
                                </Button>
                              )
                            )}
                            <Button 
                              size="sm" 
                              variant="outline-danger"
                              onClick={() => handleDelete(course)}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {canCreateCourse && (
        <CourseEditorModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onSave={handleSave}
          course={editingCourse}
        />
      )}

      {/* Suspend Course Modal */}
      <Modal show={showSuspendModal} onHide={() => !suspendSaving && setShowSuspendModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Suspend Course</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>⚠️ Warning:</strong> Suspending this course will prevent students from accessing it.
            Course managers will see a suspension notice.
          </Alert>
          <p>
            You are about to suspend: <strong>{suspendTarget?.title}</strong>
          </p>
          <Form.Group>
            <Form.Label>Suspension Reason (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Explain why this course is being suspended..."
              disabled={suspendSaving}
            />
            <Form.Text className="text-muted">
              This message will be shown to course managers.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSuspendModal(false)} disabled={suspendSaving}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleSuspendConfirm} disabled={suspendSaving}>
            {suspendSaving ? 'Suspending...' : 'Suspend Course'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
